import { $getRoot, $getSelection, $isRangeSelection, type LexicalEditor } from 'lexical'
import { useEffect, useRef, useState } from 'react'
import { RichTextEditor } from 'app/components/editor/RichTextEditor'
import { Button } from 'app/components/ui/button'
import { Dialog, DialogActionBar, DialogContent, DialogTitleBar } from 'app/components/ui/dialog'
import { Input } from 'app/components/ui/input'
import type { NoteTemplate } from 'app/generated/graphql'
import { TEMPLATE_TOKENS } from 'app/lib/messageTemplates'
import type { NoteTemplateInput } from 'app/stores/NoteTemplateStore'

type NoteTemplateEditorDialogProps = {
  open: boolean
  template?: NoteTemplate
  saving?: boolean
  onSave: (input: NoteTemplateInput) => void | Promise<void>
  onCancel: () => void
}

const BODY_HELP = `A token can carry a fallback: {{contact.firstName ?? "the caller"}}.`

export function NoteTemplateEditorDialog({ open, template, saving, onSave, onCancel }: NoteTemplateEditorDialogProps) {
  const [name, setName] = useState(``)
  const [body, setBody] = useState(``)
  const editorRef = useRef<LexicalEditor | null>(null)

  // Keyed on template?.id, not the object: a list refresh yields new references for unchanged rows.
  useEffect(() => {
    if (!open) return
    setName(template?.name ?? ``)
    setBody(template?.body ?? ``)
  }, [open, template?.id])

  const insertToken = (key: string) => {
    const editor = editorRef.current
    if (!editor) return
    editor.update(() => {
      const selection = $getSelection() ?? $getRoot().selectEnd()
      if ($isRangeSelection(selection)) selection.insertText(`{{${key}}}`)
    })
    editor.focus()
  }

  const canSave = !!name.trim() && !!body.trim() && !saving

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent
        showClose={false}
        className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden rounded-xl p-0"
      >
        <DialogTitleBar title={template ? `Edit note template` : `New note template`} />
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="note-template-name" className="text-ui-sm font-medium text-default">
              Name
            </label>
            <Input
              id="note-template-name"
              autoFocus
              value={name}
              placeholder="e.g. New patient call"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-ui-sm font-medium text-default">Note</span>
            <RichTextEditor
              key={template?.id ?? `new`}
              namespace="note-template-body"
              text={template?.body ?? ``}
              placeholder="Lay out the note the way it should read once filled in..."
              onChange={setBody}
              onReady={(editor) => (editorRef.current = editor)}
              autoFocus={false}
            />
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-subtle">Insert:</span>
              {TEMPLATE_TOKENS.map((t) => (
                <Button
                  key={t.key}
                  variant="white"
                  size="xs"
                  shape="pill"
                  title={t.description}
                  className="h-6"
                  onClick={() => insertToken(t.key)}
                >
                  {t.label}
                </Button>
              ))}
            </div>
            <p className="text-[11px] text-subtle">{BODY_HELP}</p>
          </div>
        </div>
        <DialogActionBar>
          <Button variant="secondary" size="md" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="md" disabled={!canSave} onClick={() => onSave({ name: name.trim(), body })}>
            {saving ? `Saving…` : `Save template`}
          </Button>
        </DialogActionBar>
      </DialogContent>
    </Dialog>
  )
}
